import {useAgencyQuery} from "@/api/agencyApi";
import {COMPANY_KEYS, useCompanyQuery} from "@/api/companyApi";
import CopyIcon from "@/assets/colorable/copy.svg?react";
import EyeIcon from "@/assets/colorable/eye.svg?react";
import EyeClosedIcon from "@/assets/colorable/eye_closed.svg?react";
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton";
import PreviewButton from "@/components/Common/PreviewButton";
import PrivateAmbassadorInvite from "@/components/Common/referral/PrivateAmbassadorInvite";
import ReferralCodeForm from "@/components/Common/referral/ReferralCodeForm";
import ReferralSignupForm from "@/components/Common/referral/ReferralSignupForm";
import ReferralWebsiteBadge from "@/components/Common/referral/ReferralWebsiteBadge";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import clsxm from "@/lib/clsxm";
import type {TReferralTab} from "@/store/types";
import {type FC, type ReactNode} from "react";
import {useTranslation} from "react-i18next";

const InviteAmbassadorsContent: FC<{
  agencyId?: number | null;
  companyId?: number | null;
  onSectionChange(section: TReferralTab): void;
  section: TReferralTab;
}> = ({agencyId, companyId, onSectionChange, section}) => {
  const {t} = useTranslation(["referral", "common"]);
  const isAgency = !!agencyId;
  const {data: agency} = useAgencyQuery({agencyId: agencyId!});
  const {data: company} = useCompanyQuery({companyId: companyId!});

  const isPublic = !isAgency && section === "public";
  const shareLink = `${window.location.origin}/join/${company?.slug}`;

  const {data: statistics, isLoading: isLoadingStatistics} = useGenericQuery<{
    accepted: number;
    pending_review: number;
    signed_up: number;
  }>({
    queryKey: COMPANY_KEYS.qCompanyReferralStatistics(company?.id!),
    enabled: isPublic && !!company?.id,
    staleTime: Infinity,
  });

  const publicStats = [
    {
      title: t("invite_stat_signed_up"),
      description: t("invite_stat_signed_up_description"),
      value: statistics?.signed_up ?? 0,
    },
    {
      title: t("invite_stat_pending_review"),
      description: t("invite_stat_pending_review_description"),
      value: statistics?.pending_review ?? 0,
    },
    {
      title: t("common:accepted"),
      description: t("invite_stat_accepted_description"),
      value: statistics?.accepted ?? 0,
    },
  ];

  const renderToggleCard = ({
    Icon,
    description,
    onClick,
    selected,
    title,
  }: {
    Icon: ReactNode;
    description: string;
    onClick: () => void;
    selected: boolean;
    title: string;
  }) => (
    <button
      onClick={onClick}
      className={clsxm(
        "flex min-h-20 flex-1 cursor-pointer items-center gap-3 rounded-md border px-5 py-3 transition-colors",
        selected ? "border-primary-dark-solid bg-primary-powder" : "border-primary-light-solid bg-white",
      )}>
      <div className="bg-primary-powder flex size-10 shrink-0 items-center justify-center rounded-md">{Icon}</div>
      <div className="flex flex-1 flex-col items-start gap-1">
        <span
          className={clsxm(
            "text-sm leading-2.5 font-semibold",
            selected ? "text-primary-dark-solid" : "text-neutral-black",
          )}>
          {title}
        </span>
        <span className="text-neutral-grey-dark text-left text-xs">{description}</span>
      </div>
    </button>
  );

  const shareLinkButtons = (
    <>
      <CopyToClipboardButton
        buttonStyle="White"
        className="h-8 w-fit gap-2 px-5 text-sm whitespace-nowrap"
        contentToCopy={shareLink}
        icon={<CopyIcon className="size-3.5 shrink-0" />}
        label={t("copy")}
      />
      <PreviewButton icon={null} link={shareLink} className="h-8 w-fit text-sm whitespace-nowrap" />
    </>
  );

  if (isAgency ? !agency : !company) {
    return null;
  }

  return (
    <>
      {!isAgency && (
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:gap-5">
          {renderToggleCard({
            Icon: <EyeIcon className="text-primary-dark-solid h-2.5 w-3.5" />,
            description: t("invite_toggle_public_description"),
            onClick: () => onSectionChange("public"),
            selected: isPublic,
            title: t("public"),
          })}
          {renderToggleCard({
            Icon: <EyeClosedIcon className="text-primary-dark-solid h-2 w-3.5" />,
            description: t("invite_toggle_private_description"),
            onClick: () => onSectionChange("private"),
            selected: !isPublic,
            title: t("private"),
          })}
        </div>
      )}
      {isPublic && company && (
        <>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-neutral-black text-sm font-semibold">{t("referral_activity")}</span>
            </div>
            <div className="flex flex-row items-stretch gap-3">
              {publicStats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-neutral-grey-lightest flex flex-1 flex-col items-center gap-4 rounded-sm p-2 sm:justify-center sm:py-5">
                  {isLoadingStatistics ? (
                    <SkeletonLoader className="size-10 rounded-xs" />
                  ) : (
                    <span className="text-neutral-black text-4xl font-semibold">{stat.value}</span>
                  )}
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-neutral-black text-2xs text-center leading-2 font-semibold uppercase">
                      {stat.title}
                    </span>
                    <span className="text-neutral-grey-dark text-2xs text-center leading-3.5">{stat.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <ReferralCodeForm companyId={company.id} referralCode={company.referral_code_public ?? ""} type="public" />
          <div className="flex flex-col gap-2">
            <span className="text-neutral-black text-sm font-semibold">{t("share_link")}</span>
            <div className="border-neutral-grey-light-dark bg-neutral-grey-lightest flex h-15 items-center justify-between gap-3 overflow-hidden rounded-md border py-2.5 pr-2 pl-3">
              <span className="text-neutral-black truncate text-base leading-5.5 font-medium">{shareLink}</span>
              <div className="hidden items-center gap-3 p-2 sm:flex">{shareLinkButtons}</div>
            </div>
            <div className="flex items-center gap-3 self-end sm:hidden">{shareLinkButtons}</div>
          </div>
          <ReferralWebsiteBadge link={shareLink} slug={company.slug} />
          <ReferralSignupForm slug={company.slug} />
        </>
      )}
      {!isPublic && (
        <PrivateAmbassadorInvite
          agencyId={isAgency ? agency?.id : null}
          companyId={isAgency ? null : company?.id}
          referralCode={(isAgency ? agency?.referral_code : company?.referral_code) ?? ""}
        />
      )}
    </>
  );
};

export default InviteAmbassadorsContent;
